import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Field, change } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { fieldRenderer, countryRenderer } from 'utils/formRenderers';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import { Spacing } from '@reservamos/elements';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import normalizePhone from 'utils/normalizePhone';
import getPhoneCountryNames from 'utils/phoneCountryNames';

const COMMON_COUNTRIES = ['US', 'MX', 'CO', 'BR'];

/**
 * PhoneNumberField component
 * @param {Object} props - Component props
 * @param {string} props.phoneCountryFieldName - Name of the phone country field
 * @param {string} props.phoneCodeFieldName - Name of the phone code field
 * @param {string} props.phoneFieldName - Name of the phone field
 * @param {string} props.formName - Name of the redux form
 * @param {string} props.placeholder - Placeholder text for the phone number input
 * @param {boolean} [props.isDisabled] - Flag to disable the field
 * @param {boolean} [props.hideCodeNumber] - Flag to hide the country code dropdown
 * @param {string} props.initCountry - Initial country code for the phone number input
 * @param {string} [props.autoComplete] - Value for the phone input's autocomplete attribute
 */
const PhoneNumberField = ({ ...props }) => {
  const {
    // phoneCountryFieldName, phoneCodeFieldName and phoneFieldName are needed
    // to work correctly with redux-form
    phoneCountryFieldName,
    phoneCodeFieldName,
    phoneFieldName,
    // formName is needed to change the phoneCode field
    // when the country is changed using redux-form dispatch
    formName,
    placeholder,
    isDisabled,
    hideCodeNumber,
    // initCountry is used to set the initial country of the phone number field
    // and just to use with the normalizePhone function
    initCountry,
    autoComplete,
  } = props;
  const [country, setCountry] = useState(initCountry);
  const { t, i18n } = useTranslation('general');
  const dispatch = useDispatch();
  const { ALT_COUNTRY_CODE_LABEL } = useWhitelabelFeatures();
  const countryLabels = getPhoneCountryNames(i18n.language);

  /**
   * Returns the localized country name for a given country code.
   *
   * @param {string} code - The country code.
   * @returns {string} Localized country name.
   */
  const getCountryName = (code) => countryLabels[code] || code;

  /**
   * Returns the country selector option label for a given country code.
   *
   * @param {string} countryCode - The country code.
   * @returns {string} Formatted option label. Format depends on the `ALT_COUNTRY_CODE_LABEL` feature flag:
   *   - When true:  "+52 México"
   *   - When false: "México +52".
   */
  const getCountryOptionLabel = (countryCode) => {
    const callingCode = getCountryCallingCode(countryCode);
    const countryName = getCountryName(countryCode);

    if (ALT_COUNTRY_CODE_LABEL) {
      return `+${callingCode} ${countryName}`;
    }

    return `${countryName} +${callingCode}`;
  };

  /**
   * Handles the change of the country selection.
   * Updates the country state and dispatches actions to update the phone code in the form.
   *
   * @param {Object} _ - Unused parameter.
   * @param {string} value - The selected country code.
   */
  const handleCountryChange = (_, value) => {
    const phoneCode = getCountryCallingCode(value);
    setCountry(value);
    dispatch(change(formName, phoneCodeFieldName, phoneCode));
  };

  /**
   * Normalizes the phone number based on the selected country.
   *
   * @param {string} value - The phone number to normalize.
   * @returns {string} - The normalized phone number.
   */
  const handleNormalize = (value) => {
    return normalizePhone(value, country);
  };

  const mostUsedOptions = {
    groupTitle: t('most_used'),
    subOptions: COMMON_COUNTRIES.map((countryCode) => ({
      label: getCountryOptionLabel(countryCode),
      value: countryCode,
    })),
  };

  const otherOptions = {
    groupTitle: t('others'),
    subOptions: getCountries()
      .filter((countryCode) => {
        return !COMMON_COUNTRIES.includes(countryCode);
      })
      .sort((a, b) => {
        return getCountryName(a).localeCompare(getCountryName(b), i18n.language);
      })
      .map((countryCode) => ({
        label: getCountryOptionLabel(countryCode),
        value: countryCode,
      })),
  };
  const optionsObject = [mostUsedOptions, otherOptions];

  return (
    <Spacing flexGrow isResponsive size="S">
      {!hideCodeNumber && (
        <Field
          name={phoneCountryFieldName}
          id={phoneCountryFieldName}
          component={countryRenderer}
          onChange={handleCountryChange}
          options={optionsObject}
          isDisabled={isDisabled}
        />
      )}
      <Field
        type="tel"
        name={phoneFieldName}
        id={phoneFieldName}
        placeholder={placeholder}
        component={fieldRenderer}
        normalize={handleNormalize}
        isDisabled={isDisabled}
        autoComplete={autoComplete}
      />
      <Field type="hidden" name={phoneCodeFieldName} id={phoneCodeFieldName} component="input" />
    </Spacing>
  );
};

PhoneNumberField.propTypes = {
  phoneCountryFieldName: PropTypes.string.isRequired,
  phoneCodeFieldName: PropTypes.string.isRequired,
  phoneFieldName: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  hideCodeNumber: PropTypes.bool,
  initCountry: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
};
export default PhoneNumberField;
